import React, {useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Tooltip} from 'antd';
import {DeviceRow, DisplayStatus} from '@src/dtos/device.dto';
import usePromiseFeedback from '@src/components/shared/promise-feedback/usePromiseFeedback';
import {useStores} from '@src/components/store';
import {formatPhone} from '@src/utils/phone-util';
import {EventType} from '@src/dtos/analytics.dto';
import {ErrorCode} from '@src/dtos/error-message.dto';
import {keyboardClickable} from '@src/utils/a11y-utils';
import DownloadCsvIcon from '@svg/download-csv.svg?react';

const RESEND_LIMIT_ERROR = {
    title: 'Resend Limit Reached',
    message: 'You\'ve reached the maximum number of invite resend attempts',
    okText: 'Got it'
};

interface DevicesRowActionsProps {
    row: DeviceRow;
}

export const DevicesRowActions: React.FC<DevicesRowActionsProps> = observer(({row}) => {
    const {devicesStore, uiStore} = useStores();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();
    const [isDownloading, setIsDownloading] = useState(false);

    const canResend = row.displayStatus === DisplayStatus.PENDING
        || row.displayStatus === DisplayStatus.SENT;
    const canDownloadVpnUsage = !!row.vpnUsage && !!row.deviceId;

    const onResend = () => {
        uiStore.sendGeneralEvent(EventType.LinkClicked, { button: 'resendInvite', location: 'mobilityConnectionPage' });
        return promiseFeedbackFactory({
            promise: devicesStore.resend(row.inviteId),
            notifObj: {show: true, notificationMsg: `Resent invite to ${formatPhone(row.phoneNumber)}`},
            errorsOverride: {
                [ErrorCode.EXCEED_COUNT.toString()]: RESEND_LIMIT_ERROR
            }
        });
    };

    const onDownloadVpnUsage = () => {
        if (isDownloading) {
            return;
        }

        uiStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'downloadVPNUsage',
            location: 'devicesPage',
        });
        setIsDownloading(true);
        const downloadPromise = devicesStore.downloadVpnUsage(row)
            .finally(() => setIsDownloading(false));
        promiseFeedbackFactory({
            promise: downloadPromise,
            notifObj: {show: false},
        });
    };

    return <div className="devices-row-actions">
        {canDownloadVpnUsage &&
            <Tooltip title="Download VPN usage" placement="bottom" overlayClassName="cru-device-menu-tooltip">
                <button type="button"
                        className={`action-download-vpn-usage${isDownloading ? ' is-downloading' : ''}`}
                        aria-label="Download VPN usage"
                        aria-disabled={isDownloading}
                        onClick={onDownloadVpnUsage}>
                    <DownloadCsvIcon/>
                </button>
            </Tooltip>}
        {canResend &&
            <span className="action-resend" {...keyboardClickable(onResend)}>Resend invite</span>}
        {promiseContextHolder}
    </div>;
});
